import React, { useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageHero from '@/components/ui/page-hero';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Lightbulb, Bot, Settings, ArrowRight, Check, Search, PieChart, Users, BrainCircuit } from 'lucide-react';
import PageSection from '@/components/ui/page-section';
import SectionHeader from '@/components/ui/section-header';
import { Link } from 'react-router-dom';
import ContactDialog from '@/components/ContactDialog';

const Services: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const coreServices = [
    {
      icon: <Lightbulb className="h-10 w-10 text-blue-600" />,
      title: "AI Strategy & Consulting",
      description: "AI Readiness Assessments, Education & Custom AI Integration Plans",
      features: [
        "AI readiness evaluation",
        "Custom integration planning",
        "Team training & education",
        "ROI projections & strategy"
      ],
      href: "/ai-consulting"
    },
    {
      icon: <Bot className="h-10 w-10 text-blue-600" />,
      title: "AI-Powered Personas & Avatars",
      description: "Digital Avatars & AI-driven customer engagement tools",
      features: [
        "Customer engagement avatars",
        "Qualitative research personas",
        "AI interviewer technology",
        "Conversation design"
      ],
      href: "/ai-apps"
    },
    {
      icon: <Settings className="h-10 w-10 text-blue-600" />,
      title: "AI Automation & Business Tools",
      description: "AI for Sales, Marketing & Lead Generation",
      features: [
        "Workflow automation",
        "Content generation",
        "Lead qualification & scoring",
        "Customer insights & analytics"
      ],
      href: "/ai-apps"
    }
  ];

  const implementationProcess = [
    "AI Strategy Development",
    "Proof of Concept",
    "Full Development & Integration",
    "Deployment & Training",
    "Optimization & Scaling"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <PageHero 
        badge="Our Services"
        title="AI Solutions for Business Growth & Innovation"
        description="From AI strategy consulting to full-stack AI development, we help businesses innovate, automate, and scale with cutting-edge AI solutions."
        buttons={[
          {
            text: "Book a Discovery Call",
            variant: "default",
            component: (
              <ContactDialog
                trigger={
                  <Button variant="default">Book a Discovery Call</Button>
                }
                title="Book a Discovery Call"
                description="Fill out the form below and we'll get back to you within 24 hours."
              />
            )
          },
          {
            text: "Explore Beta Program",
            variant: "outline",
            href: "/beta-signup"
          }
        ]}
      />
      
      {/* Core Services Section */}
      <PageSection className="py-20">
        <SectionHeader
          badge="What We Do"
          title="We build practical AI solutions that solve real business problems and create measurable value."
          description="We build practical AI solutions that solve real business problems and create measurable value."
          centered={true}
          badgeClassName="text-white"
        />
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {coreServices.map((service, index) => (
            <Card key={index} className="p-8 border shadow-lg hover:shadow-xl transition-all">
              <div className="flex flex-col items-center text-center">
                <div className="mb-5 p-3 rounded-full bg-blue-100 inline-flex items-center justify-center w-16 h-16">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold mb-3">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                
                <ul className="space-y-2 text-left w-full mb-6">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-start">
                      <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Button variant="outline" size="sm" className="mt-auto group" asChild>
                  <Link to={service.href}>
                    Learn more 
                    <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </div>
            </Card>
          ))}
        </div>
        
        <div className="text-center">
          <ContactDialog
            trigger={
              <Button size="lg" className="btn-hover-effect group">
                Book a Free Consultation
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            }
            title="Book a Free Consultation"
            description="Fill out the form below and we'll get back to you within 24 hours."
          />
        </div>
      </PageSection>
      
      {/* AI-Driven Market Research Section */}
      <PageSection className="py-20 bg-gray-50">
        <SectionHeader
          badge="PersonaAI"
          title="AI-Driven Market Research"
          description="Uncover deeper customer insights faster and more cost-effectively with our AI-powered market research technology."
          centered={true}
          badgeClassName="text-white"
        />
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <div className="bg-white p-8 rounded-xl shadow-md">
              <h3 className="text-xl font-bold mb-4">Transform Your Market Research</h3>
              <p className="text-gray-600 mb-6">
                Traditional market research is slow and expensive. PersonaAI uses advanced AI personas—trained to think and behave like real consumers—to deliver deep insights at speed and scale.
              </p>
              
              <div className="space-y-4">
                {[
                  {
                    icon: <Search className="h-5 w-5 text-blue-600" />,
                    title: "AI-Driven Focus Groups",
                    description: "Simulated consumer discussions, tested for accuracy against real-world participants."
                  },
                  {
                    icon: <Users className="h-5 w-5 text-blue-600" />,
                    title: "Custom AI Personas",
                    description: "AI models trained to reflect your target audience for continuous insights."
                  },
                  {
                    icon: <BrainCircuit className="h-5 w-5 text-blue-600" />,
                    title: "Consumer Behavior Prediction",
                    description: "AI models that anticipate trends before they happen."
                  },
                  {
                    icon: <PieChart className="h-5 w-5 text-blue-600" />,
                    title: "Qualitative + Quantitative Research",
                    description: "AI that translates deep human conversations into data-driven insights."
                  }
                ].map((item, index) => (
                  <div key={index} className="flex items-start">
                    <div className="bg-blue-100 p-2 rounded-full mr-4 flex-shrink-0">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">{item.title}</h4>
                      <p className="text-gray-600 text-sm">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <div className="space-y-6">
            <h3 className="text-2xl font-bold">Who It's For:</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  icon: <PieChart className="h-10 w-10 text-white" />,
                  title: "Brands & Enterprises",
                  description: "Understand customers better, refine messaging, and optimize product-market fit.",
                  gradient: "from-blue-600 to-blue-800"
                },
                {
                  icon: <BrainCircuit className="h-10 w-10 text-white" />,
                  title: "Startups & Web3 Projects",
                  description: "Rapid market validation without the high costs.",
                  gradient: "from-purple-600 to-purple-800"
                },
                {
                  icon: <Users className="h-10 w-10 text-white" />,
                  title: "Agencies & Researchers",
                  description: "Scale qualitative research faster than ever before.",
                  gradient: "from-teal-600 to-teal-800"
                },
                {
                  icon: <Search className="h-10 w-10 text-white" />,
                  title: "Product Teams",
                  description: "Test concepts and features with AI-simulated target users.",
                  gradient: "from-orange-600 to-orange-800"
                }
              ].map((audience, index) => (
                <div key={index} className={`bg-gradient-to-br ${audience.gradient} p-6 rounded-xl text-white`}>
                  <div className="bg-white/20 p-3 rounded-full w-16 h-16 flex items-center justify-center mb-4">
                    {audience.icon}
                  </div>
                  <h4 className="text-xl font-bold mb-2">{audience.title}</h4>
                  <p className="text-white/90">{audience.description}</p>
                </div>
              ))}
            </div>
            
            <div className="text-center mt-8">
              <Button size="lg" variant="default" className="btn-hover-effect group" asChild>
                <a href="http://personaresearch.ai" target="_blank" rel="noopener noreferrer">
                  Learn More About PersonaAI
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </Button>
            </div>
          </div>
        </div>
      </PageSection>
      
      {/* Implementation Process */}
      <PageSection className="py-20 bg-gray-900 text-white">
        <SectionHeader
          badge="Implementation"
          title="Our Implementation Process"
          description="We follow a proven methodology to ensure successful AI implementation with minimal disruption to your business."
          centered={true}
          badgeClassName="text-white"
        />
        
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-16">
          {implementationProcess.map((step, index) => (
            <div key={index} className="relative">
              <div className="flex flex-col items-center">
                <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mb-4 z-10">
                  {index + 1}
                </div>
                <h3 className="text-lg font-semibold text-center mb-2">{step}</h3>
              </div>
              {index < implementationProcess.length - 1 && (
                <div className="absolute top-6 left-1/2 w-full h-0.5 bg-blue-600/30 -z-0 hidden md:block"></div>
              )}
            </div>
          ))}
        </div>
        
        <div className="text-center">
          <p className="text-lg text-white/80 max-w-3xl mx-auto mb-8">
            Our structured approach ensures that we deliver AI solutions that integrate seamlessly with your existing systems and processes, with clear milestones and deliverables at every stage.
          </p>
          <ContactDialog
            trigger={
              <Button variant="default" size="lg" className="btn-hover-effect">
                Start Your AI Journey
              </Button>
            }
            title="Start Your AI Journey"
            description="Fill out the form below, and we'll help you get started with AI solutions tailored to your needs."
          />
        </div>
      </PageSection>
      
      {/* Beta Program Section */}
      <PageSection id="beta" className="py-20 bg-gray-100">
        <SectionHeader
          badge="Limited Availability"
          title="Try AI for Your Business – Join Our Beta Program"
          description="Gain early access to cutting-edge AI solutions before they hit the market. Get exclusive benefits as a beta tester."
          centered={true}
          badgeClassName="text-white"
        />
        
        <div className="max-w-3xl mx-auto bg-white p-8 rounded-xl shadow-lg mb-16">
          <h3 className="text-xl font-bold mb-6 text-center">What You Get As A Beta Partner</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="flex items-start">
              <div className="bg-blue-100 p-2 rounded-full mr-4">
                <Check className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">Early Access</h4>
                <p className="text-gray-600 text-sm">Be the first to use our AI chatbots & voice assistants</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="bg-blue-100 p-2 rounded-full mr-4">
                <Check className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">Custom Setup</h4>
                <p className="text-gray-600 text-sm">Get personalized integration support for your systems</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="bg-blue-100 p-2 rounded-full mr-4">
                <Check className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">Special Pricing</h4>
                <p className="text-gray-600 text-sm">Enjoy reduced rates for early adopters</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="bg-blue-100 p-2 rounded-full mr-4">
                <Check className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">Priority Support</h4>
                <p className="text-gray-600 text-sm">Get dedicated onboarding and technical assistance</p>
              </div>
            </div>
          </div>
          
          <div className="text-center">
            <Button 
              size="lg" 
              className="btn-hover-effect group"
              asChild
            >
              <Link to="/beta-signup">
                Apply for Beta Access
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </div>
        </div>
      </PageSection>
      
      {/* CTA Section */}
      <div id="contact" className="py-20 px-6 md:px-10 bg-gradient-to-br from-primary/20 to-primary/10">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Build Your AI Solution?</h2>
          <p className="text-lg text-foreground/80 max-w-2xl mx-auto mb-10">
            Schedule a consultation with our team to discuss how our AI services can help you achieve your business goals.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <ContactDialog
              trigger={
                <Button size="lg" variant="consultation" className="btn-hover-effect">
                  Book a Discovery Call
                </Button>
              }
              title="Book a Discovery Call"
              description="Fill out the form below and our team will contact you to schedule a call."
            />
            <Button size="lg" variant="outline" asChild>
              <Link to="/ai-apps">
                Explore Our AI Apps
              </Link>
            </Button>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Services;
